import React, { useState } from "react";
import axios from "axios";
import { Mail, Phone, Linkedin, Calendar, Check } from "lucide-react";
import { toast } from "sonner";
import { BRAND } from "../lib/constants";
import { useSEO } from "../lib/seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CONCERNS = [
  "Medicaid Pending & AR Recovery",
  "Survey Readiness",
  "Operational Stabilization",
  "Litigation Support",
  "Speaking & Training",
  "General Inquiry",
];

const initialForm = {
  name: "",
  facility: "",
  role: "",
  email: "",
  phone: "",
  state: "",
  concern: "",
  message: "",
};

export default function Contact() {
  useSEO({
    title:
      "Book a Consultation · Healthcare Compliance Consultant · St. Louis, MO",
    description:
      "Book a confidential 30-minute consultation with Sierra Thomas, AthenaCrest Defense. Healthcare operations and compliance consulting for Missouri skilled nursing facilities and long-term care operators.",
    path: "/contact",
  });
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please provide your name, email, and a message.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSubmitted(true);
      setForm(initialForm);
      toast.success("Inquiry received. Sierra's team will be in touch shortly.");
    } catch (err) {
      toast.error("Something went wrong. Please try again or email us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page" className="bg-ivory">
      <section className="section-pad pt-32 bg-paper">
        <div className="container-luxe max-w-5xl">
          <div className="overline mb-5">Contact · St. Louis, Missouri</div>
          <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl text-navy leading-[0.95] mb-6">
            A confidential<br />
            <span className="italic text-teal">conversation.</span>
          </h1>
          <p className="font-sans text-lg text-navy/75 max-w-3xl leading-relaxed">
            Engagements begin with a 30 minute consultation. Book directly on
            Sierra's calendar or share the details of your inquiry through the
            form below. Every conversation is confidential. AthenaCrest Defense
            is based in St. Louis, Missouri and serves facilities statewide.
          </p>
        </div>
      </section>

      {/* CONTACT INFO */}
      <section className="py-12 border-b border-champagne-200" data-testid="contact-info">
        <div className="container-luxe">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <a
              href={`mailto:${BRAND.email}`}
              className="flex items-start gap-4 group"
              data-testid="contact-email"
            >
              <div className="w-12 h-12 border border-gold flex items-center justify-center group-hover:bg-gold transition-colors">
                <Mail className="h-5 w-5 text-gold group-hover:text-navy" />
              </div>
              <div>
                <div className="overline text-[10px] mb-1">Email</div>
                <div className="font-sans text-base text-navy group-hover:text-teal transition-colors">
                  {BRAND.email}
                </div>
              </div>
            </a>
            <a
              href={`tel:${BRAND.phone}`}
              className="flex items-start gap-4 group"
              data-testid="contact-phone"
            >
              <div className="w-12 h-12 border border-gold flex items-center justify-center group-hover:bg-gold transition-colors">
                <Phone className="h-5 w-5 text-gold group-hover:text-navy" />
              </div>
              <div>
                <div className="overline text-[10px] mb-1">Direct</div>
                <div className="font-sans text-base text-navy group-hover:text-teal transition-colors">
                  {BRAND.phone}
                </div>
              </div>
            </a>
            <a
              href={BRAND.linkedin}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-start gap-4 group"
              data-testid="contact-linkedin"
            >
              <div className="w-12 h-12 border border-gold flex items-center justify-center group-hover:bg-gold transition-colors">
                <Linkedin className="h-5 w-5 text-gold group-hover:text-navy" />
              </div>
              <div>
                <div className="overline text-[10px] mb-1">LinkedIn</div>
                <div className="font-sans text-base text-navy group-hover:text-teal transition-colors">
                  Sierra Thomas
                </div>
              </div>
            </a>
          </div>
        </div>
      </section>

      {/* CALENDLY + FORM */}
      <section className="section-pad" data-testid="booking-section">
        <div className="container-luxe">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
            {/* CALENDLY */}
            <div data-testid="calendly-section">
              <div className="overline mb-5">Book a 30 Minute Consultation</div>
              <h2 className="font-serif text-3xl sm:text-4xl text-navy leading-tight mb-6">
                Select a time directly on Sierra's calendar.
              </h2>
              <p className="font-sans text-base text-navy/65 mb-8 leading-relaxed">
                Confidential by default. Most calls are scoped within the first
                fifteen minutes. The remaining time is used to map the highest
                priority operational risk.
              </p>
              <div
                className="border border-champagne-200 bg-ivory overflow-hidden"
                style={{ minHeight: 680 }}
              >
                <iframe
                  src={`${BRAND.calendlyUrl}?hide_event_type_details=0&hide_gdpr_banner=1&primary_color=0a1128`}
                  width="100%"
                  height="680"
                  frameBorder="0"
                  title="Book a consultation"
                  data-testid="calendly-iframe"
                />
              </div>
              <a
                href={BRAND.calendlyUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-ghost mt-6 inline-flex"
                data-testid="calendly-open-external"
              >
                <Calendar className="h-4 w-4 mr-2" />
                Open in Calendly
              </a>
            </div>

            {/* FORM */}
            <div data-testid="form-section">
              <div className="overline mb-5">Or, Send an Inquiry</div>
              <h2 className="font-serif text-3xl sm:text-4xl text-navy leading-tight mb-6">
                Tell us about the facility.
              </h2>
              <p className="font-sans text-base text-navy/65 mb-8 leading-relaxed">
                Share the operational concern most on your mind. Sierra's team
                will respond within one business day.
              </p>

              {submitted ? (
                <div
                  className="bg-teal text-ivory p-10 border border-gold/40"
                  data-testid="form-success"
                >
                  <Check className="h-10 w-10 text-gold mb-4" />
                  <h3 className="font-serif text-3xl mb-3">Thank you.</h3>
                  <p className="font-sans text-ivory/85 leading-relaxed">
                    Your inquiry has been received. Sierra's team will be in
                    touch within one business day. For urgent litigation
                    matters, please call directly.
                  </p>
                  <button
                    onClick={() => setSubmitted(false)}
                    className="mt-6 text-sm uppercase tracking-wide text-gold hover:text-ivory transition-colors"
                    data-testid="form-reset"
                  >
                    Submit another inquiry →
                  </button>
                </div>
              ) : (
                <form
                  onSubmit={submit}
                  className="space-y-5"
                  data-testid="contact-form"
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <FieldInput
                      label="Name"
                      value={form.name}
                      onChange={update("name")}
                      required
                      testid="form-name"
                    />
                    <FieldInput
                      label="Facility / Organization"
                      value={form.facility}
                      onChange={update("facility")}
                      testid="form-facility"
                    />
                    <FieldInput
                      label="Role / Title"
                      value={form.role}
                      onChange={update("role")}
                      testid="form-role"
                    />
                    <FieldInput
                      label="State"
                      value={form.state}
                      onChange={update("state")}
                      testid="form-state"
                    />
                    <FieldInput
                      label="Email"
                      type="email"
                      value={form.email}
                      onChange={update("email")}
                      required
                      testid="form-email"
                    />
                    <FieldInput
                      label="Phone"
                      type="tel"
                      value={form.phone}
                      onChange={update("phone")}
                      testid="form-phone"
                    />
                  </div>

                  <div>
                    <label className="block overline text-[10px] mb-2">
                      Primary Concern
                    </label>
                    <select
                      value={form.concern}
                      onChange={update("concern")}
                      className="w-full bg-ivory border border-champagne-200 px-4 py-3 font-sans text-sm text-navy focus:outline-none focus:border-gold rounded-none"
                      data-testid="form-concern"
                    >
                      <option value="">Select a concern</option>
                      {CONCERNS.map((c) => (
                        <option key={c} value={c}>
                          {c}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block overline text-[10px] mb-2">
                      Message *
                    </label>
                    <textarea
                      value={form.message}
                      onChange={update("message")}
                      required
                      rows={5}
                      placeholder="Tell us where the facility hurts most."
                      className="w-full bg-ivory border border-champagne-200 px-4 py-3 font-sans text-sm text-navy placeholder:text-navy/40 focus:outline-none focus:border-gold rounded-none resize-none"
                      data-testid="form-message"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={submitting}
                    className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="form-submit"
                  >
                    {submitting ? "Sending…" : "Send Confidential Inquiry"}
                  </button>

                  <p className="font-sans text-xs text-navy/50 leading-relaxed pt-3">
                    By submitting this form, you acknowledge that AthenaCrest
                    Defense provides consulting services only and that submission
                    does not establish an attorney-client relationship.
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function FieldInput({ label, type = "text", value, onChange, required, testid }) {
  return (
    <div>
      <label className="block overline text-[10px] mb-2">
        {label} {required && "*"}
      </label>
      <input
        type={type}
        value={value}
        onChange={onChange}
        required={required}
        className="w-full bg-ivory border border-champagne-200 px-4 py-3 font-sans text-sm text-navy placeholder:text-navy/40 focus:outline-none focus:border-gold rounded-none"
        data-testid={testid}
      />
    </div>
  );
}
